import { api } from '@/config/api';

export interface FbInsightsRow {
  impressions?: string; clicks?: string; spend?: string; ctr?: string; cpc?: string; cpm?: string; reach?: string;
}

export interface GeoKeyItem { key: string; }
export interface InterestItem { id: string; name: string; }
export interface GeoLocations {
  countries?: string[];
  cities?: GeoKeyItem[];
  regions?: GeoKeyItem[];
  custom_locations?: Record<string, unknown>[];
}

export interface FbTargeting {
  geo_locations?: GeoLocations;
  countries?: string[];
  age_min?: number;
  age_max?: number;
  genders?: number[];
  interests?: InterestItem[];
  custom_audiences?: string[];
  excluded_custom_audiences?: string[];
  advantage_audience?: 0 | 1;
  // Placement fields. When `publisher_platforms` is undefined the ad set is
  // running on Meta's automatic placements (all eligible surfaces).
  publisher_platforms?: string[];
  facebook_positions?: string[];
  instagram_positions?: string[];
  messenger_positions?: string[];
  audience_network_positions?: string[];
  device_platforms?: string[];
}

/**
 * Resolve which platforms an ad set actually runs on. When the user picked
 * "automatic" placements, Meta's `publisher_platforms` is omitted — that
 * means all platforms are eligible. When manual, only the listed ones run.
 */
export function getAdSetPlacements(s: FbAdSet | undefined | null): {
  isAutomatic: boolean;
  facebook: boolean;
  instagram: boolean;
  messenger: boolean;
  audienceNetwork: boolean;
} {
  const pp = s?.targeting?.publisher_platforms;
  if (!pp || pp.length === 0) {
    return { isAutomatic: true, facebook: true, instagram: true, messenger: true, audienceNetwork: true };
  }
  return {
    isAutomatic: false,
    facebook: pp.includes('facebook'),
    instagram: pp.includes('instagram'),
    messenger: pp.includes('messenger'),
    audienceNetwork: pp.includes('audience_network'),
  };
}

export interface FbAdSet {
  id: string; name: string;
  campaign_id: string;
  status: string; effective_status: string;
  billing_event?: string; optimization_goal?: string; bid_amount?: string;
  daily_budget?: string; lifetime_budget?: string; budget_remaining?: string;
  start_time?: string; end_time?: string;
  created_time?: string; updated_time?: string;
  targeting?: FbTargeting;
  promoted_object?: Record<string, string>;
  destination_type?: string;
  attribution_spec?: Array<{ event_type: string; window_days: number }>;
  insights?: { data: FbInsightsRow[] };
}

export interface CreateAdSetPayload {
  expected_account_uid?: string;
  name: string;
  campaign_id: string;
  status?: 'ACTIVE' | 'PAUSED';
  billing_event: string;
  optimization_goal: string;
  bid_amount?: string;
  daily_budget?: string;
  lifetime_budget?: string;
  start_time?: string;
  end_time?: string;
  targeting: FbTargeting;
  promoted_object?: Record<string, string>;
  destination_type?: string;
  attribution_spec?: Array<{ event_type: string; window_days: number }>;
}

export const facebookAdSetsService = {
  byCampaign: async (campaignId: string, refresh = false) =>
    (await api.get(`/slighter_crm/facebook/ad-sets/by-campaign/${campaignId}`, { params: { refresh: refresh ? '1' : undefined } })).data as { data: FbAdSet[]; fetchedAt: string; fromCache: boolean },
  get: async (adSetId: string, refresh = false): Promise<FbAdSet> =>
    (await api.get(`/slighter_crm/facebook/ad-sets/${adSetId}`, { params: { refresh: refresh ? '1' : undefined } })).data,
  create: async (payload: CreateAdSetPayload): Promise<{ id: string }> =>
    (await api.post('/slighter_crm/facebook/ad-sets', payload)).data,
  pause: async (adSetId: string) => (await api.post(`/slighter_crm/facebook/ad-sets/${adSetId}/pause`)).data,
  resume: async (adSetId: string) => (await api.post(`/slighter_crm/facebook/ad-sets/${adSetId}/resume`)).data,
  archive: async (adSetId: string) => (await api.post(`/slighter_crm/facebook/ad-sets/${adSetId}/archive`)).data,
};

export function getAdSetInsights(s: FbAdSet): FbInsightsRow | null {
  return s.insights?.data?.[0] ?? null;
}
